import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useAdminAuth } from "../context/AdminAuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Что-то пошло не так. Попробуйте ещё раз.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="noise-overlay" />
      <div className="w-full max-w-sm bg-[#0A0A0C] border border-white/10 rounded-2xl p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-[#00F0FF]" strokeWidth={1.5} />
          </div>
          <h1 className="font-display font-semibold text-xl text-white">Панель администратора</h1>
          <p className="mt-2 text-sm text-zinc-500">Войдите, чтобы увидеть статистику сайта</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@neuroscanapp.io"
              required
              data-testid="admin-login-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Пароль</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="pl-9"
                data-testid="admin-login-password-input"
              />
            </div>
          </div>

          {error && <p className="text-xs text-red-400" data-testid="admin-login-error">{error}</p>}

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full bg-[#00F0FF] text-black hover:brightness-110 font-semibold"
          >
            {loading ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Вход...</span>
            ) : (
              "Войти"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
